import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Header from "./Header";
import InfoCard from "./InfoCard";
import CalendarView from "./CalendarView";
import AddHolidayForm from "./AddHolidayForm";
import MonthlyHolidaysList from "./MonthlyHolidaysList";
import WorkingDaysSchedule from "./WorkingDaysSchedule";
import WorkingHoursSettings from "./WorkingHoursSettings";
import {
  DayOfWeek,
  Holiday,
  HolidayApiResponse,
  WorkingHoursMap,
} from "../../types";
import { useHolidayList, useWorkingist } from "../../hooks";
import { transformHolidayApiToMap } from "./utils";
import { transformToWorkingHoursMap } from "./utils";
import { SharedProvider } from "@shared/context/SharedContext";
import { usePageViewTimer } from "@shared/hooks/usePageViewTimer";

const CalenderLanding: React.FC = () => {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  usePageViewTimer({ pageName: "ONB Calender & Scheduling" });
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(today);
  const [selectedHoliday, setSelectedHoliday] = useState<Holiday | null>(null); // ✅ new
  const [workingHours, setWorkingHours] = useState<WorkingHoursMap | null>(
    null
  );
  const [selectedDay, setSelectedDay] = useState<DayOfWeek>("monday");
  const [error, setError] = useState("");

  // Holidays
  const { data: holidayData, isLoading: isHolidayLoading } =
    useHolidayList(currentDate);
  const holidays = holidayData as HolidayApiResponse;
  const holidayMap = holidays ? transformHolidayApiToMap(holidays) : {};

  // Working hours
  const { data: workingHoursData, isLoading: isWorkingLoading } =
    useWorkingist();

  useEffect(() => {
    if (workingHoursData?.result) {
      const map = transformToWorkingHoursMap(workingHoursData.result);
      setWorkingHours(map);
    }
  }, [workingHoursData]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const navigateMonth = (step: number) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + step);
    setCurrentDate(newDate);
  };

  const handleHolidaySelect = (holiday: Holiday) => {
    setSelectedDate(new Date(holiday.date));
    setSelectedHoliday(holiday); // ✅ new

    if (
      new Date(holiday.date).getMonth() !== currentDate.getMonth() ||
      new Date(holiday.date).getFullYear() !== currentDate.getFullYear()
    ) {
      setCurrentDate(
        new Date(
          new Date(holiday.date).getFullYear(),
          new Date(holiday.date).getMonth(),
          1
        )
      );
    }

    setTimeout(() => {
      const addHolidayForm = document.getElementById("add-holiday-form");
      if (addHolidayForm) {
        addHolidayForm.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setSelectedHoliday(null); // ✅ reset if not coming from list
    setTimeout(() => {
      const addHolidayForm = document.getElementById("add-holiday-form");
      if (addHolidayForm) {
        addHolidayForm.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen">
      <div className="flex flex-col gap-2">
        <div className="flex flex-col">
          <Header />
          <InfoCard />
        </div>

        <div className="mb-12">
          <div className="flex flex-col  mb-6">
            <p className="page-subheading">Holiday Calendar</p>
            <p className="page-subtitle">
              Manage holidays and non-working days for your utility
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left Section: Calendar + Form */}
            <div className="border rounded-lg shadow-md p-4 bg-white flex gap-6 lg:basis-[70%] w-full">
              {/* Calendar View */}
              <div className="rounded-lg bg-white flex-1">
                <div className="flex justify-between items-center mb-4">
                  <button
                    onClick={() => navigateMonth(-1)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-lg font-medium text-gray-700">
                    {monthNames[currentDate.getMonth()]}{" "}
                    {currentDate.getFullYear()}
                  </span>
                  <button
                    onClick={() => navigateMonth(1)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                <CalendarView
                  currentDate={currentDate}
                  selectedDate={selectedDate}
                  onDateSelect={handleDateSelect}
                  holidays={holidayMap}
                />
              </div>

              {/* Add Holiday Form */}
              <div
                id="add-holiday-form"
                className="rounded-lg bg-gray-50 flex-1"
              >
                <AddHolidayForm
                  selectedDate={selectedDate}
                  holidays={holidays}
                  holidayMap={holidayMap}
                  error={error}
                  onRemoveHoliday={() => {}}
                  selectedHoliday={selectedHoliday} // ✅ passed here
                />
              </div>
            </div>

            {/* Right Section: Holiday List */}
            <div className="border rounded-lg shadow-md bg-white lg:basis-[30%] w-full">
              {holidays?.result && (
                <MonthlyHolidaysList
                  currentDate={currentDate}
                  holidays={holidays}
                  onHolidaySelect={handleHolidaySelect} // ✅ pass full holiday
                  onRemoveHoliday={() => {}}
                />
              )}
            </div>
          </div>
        </div>

        {/* Working Schedule + Hours */}
        <SharedProvider>
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="lg:basis-[70%] w-full">
              <WorkingDaysSchedule />
            </div>
            <div className="lg:basis-[30%] w-full">
              <WorkingHoursSettings />
            </div>
          </div>
        </SharedProvider>
      </div>
    </div>
  );
};

export default CalenderLanding;
